import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { CompanyServices } from "../../../core/services/CompanyServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { CompanyFields, CompanyListParams } from "./CompanyDataTypes";
import { Permission } from "../../../core/types/PermissionInterface";
import "./Companies.css";
import CompanyFilters from "./CompanyFilters";
import { isEmptyVal, isNotEmpty, displayField } from "../../../utils/helpers";
import ImageCellFormatter from "../../components/Elements/ImageCellFormatter";
import AddressCell from "../../components/Elements/AddressCell";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import CompanySingleAction from "./CompanySingleAction";
import CompanyBulkAction from "./CompanyBulkAction";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import {
  statusArr,
  messageTypeArr,
  appConstObj,
  roleTypeArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import OwnerCell from "./OwnerCell";
import ContactPersonCell from "../../components/Elements/ContactPersonCell";
import PhoneCell from "../../components/Elements/PhoneCell";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => void;
}

function Companies() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CompanyFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const modalRef = useRef<ImageModal>(null);
  const [selectedCompany, setSelectedCompanyData] = useState<any>({});
  const [openPictureDelModal, setOpenPictureDelModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const initSearchFormData = {
    name: "",
    code: "",
    type: "",
    building: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    mobile: "",
    contact_person: "",
    contact_person_email: "",
    contact_person_phone: "",
    prime_comp_admin: "",
    prime_comp_admin_email: "",
    prime_comp_admin_phone: "",
    status: "",
    smart_search: "",
  };

  const [formData, setFormData] =
    useState<CompanyListParams>(initSearchFormData);
  const [showCreate, setShowCreate] = useState<boolean>();
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;
  const [failedImages, setFailedImages] = useState<any>([]);

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };

  async function getCompanies(params: CompanyListParams) {
    try {
      setLoading(true);
      const res = await CompanyServices.getCompanies(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  function init() {
    setShowCreate(UtilityServices.hasPermission("create-company"));
  }

  useEffect(() => {
    init();
    getCompanies(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }, []);

  async function deleteCompany(company: any) {
    try {
      let res = await CompanyServices.deleteCompany(company?.current?.id);
      setPage(1);
      getCompanies(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function publishCompany(company: any) {
    try {
      let status =
        company?.current?.status == statusArr.inactive
          ? statusArr.active
          : statusArr.inactive;

      let res = await CompanyServices.publishCompany(company?.current?.id, {
        status,
      });
      setPage(1);
      getCompanies(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function refreshListData() {
    try {
      getCompanies(getSearchParams());
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function exportCompanyAction() {
    try {
      await CompanyServices.exportCompanies(getSearchParams());
    } catch (error) {
      console.log(error);
    }
  }

  async function deletePic() {
    try {
      setOpenPictureDelModal(false);

      let res = await CompanyServices.deleteCompanyLogo(selectedCompany.id);
      setPage(1);
      getCompanies(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getFailedImages(val: any) {
    setFailedImages(val);
  }

  function companyPicModal(value: any) {
    if (
      !failedImages.includes(value.large_logo_url) &&
      value.large_logo_url != null &&
      value.large_logo_url !== ""
    ) {
      setSelectedCompanyData(value);
      modalRef.current?.openModal();
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: CompanyListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getCompanies(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getCompanies(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getCompanies(getSearchParams(formData));
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getCompanies(
      getSearchParams({
        ...initSearchFormData,
        ...{
          page: 1,
          rows: appConstObj.record_per_page,
        },
      })
    );
  }

  const filterCompanies = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getCompanies(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getCompanies(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getCompanies(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const publishPermission = UtilityServices.hasPermission("publish-company");
  const updatePermission = UtilityServices.hasPermission("update-company");
  const deletePermission = UtilityServices.hasPermission("delete-company");
  const createUserPermission = UtilityServices.hasPermission("create-user");
  const importProductPermission = UtilityServices.hasPermission(
    "import-product-for-company"
  );
  const permissionObj: Permission = {
    publish: publishPermission,
    update: updatePermission,
    delete: deletePermission,
    create_user: createUserPermission,
    import_product: importProductPermission,
  };

  return (
    <div className="companies">
      <Container className="container">
        <Header className="header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4>{t("COMPANIES")}</h4>
            </FlexboxGrid.Item>
            {showCreate && (
              <FlexboxGrid.Item colspan={4} style={{ textAlign: "end" }}>
                <Button
                  appearance="primary"
                  onClick={() => navigate(ContentRoutes.CREATE_COMPANY)}
                >
                  {t("CREATE")}
                </Button>
              </FlexboxGrid.Item>
            )}
          </FlexboxGrid>
        </Header>
        <Content className="customers">
          {UtilityServices.hasLoggedInUserType(roleTypeArr.backend) && (
            <>
              <CompanyFilters onSubmit={submitSearch} onClear={clearSearch} />
              <div className="row">
                <CompanyBulkAction
                  checkedKeys={checkedKeys}
                  setCheckedKeys={setCheckedKeys}
                  onExportCompany={exportCompanyAction}
                />
                <div className="col-md-4">
                  <InputGroup>
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity:
                          t("COMPANY").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase() +
                          ", " +
                          t("CODE").toLowerCase() +
                          ", " +
                          t("OR").toLowerCase() +
                          " " +
                          t("PHONE").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterCompanies(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterCompanies(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                  </InputGroup>
                </div>
              </div>
            </>
          )}

          <div className={total > 0 ? "company-table" : "no-record-table"}>
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
            >
              <Column width={40} align="center" fixed>
                <CustomHeaderCell>
                  <div>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </CustomHeaderCell>
                <CheckCell
                  dataKey="id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>
              <Column width={60} align="center" fixed>
                <CustomHeaderCell>{t("LOGO")}</CustomHeaderCell>
                <ImageCellFormatter
                  dataKey="large_logo_url"
                  rowData={(rowData: any) => rowData}
                  dummyImage={loggedInUserDetail.company_default_logo}
                  onFailedImages={getFailedImages}
                  openPictureModal={companyPicModal}
                />
              </Column>
              <Column width={180} sortable resizable fixed>
                <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                <CustomCell dataKey="comp_name">
                  {(rowData: any) => (
                    <span
                      title={
                        (isNotEmpty(`${rowData.comp_code}`) &&
                          `${rowData.comp_code}`) ||
                        `${rowData.comp_name}`
                      }
                    >
                      {displayField(rowData.comp_name)}
                    </span>
                  )}
                </CustomCell>
              </Column>

              <Column width={100} sortable resizable fixed>
                <CustomHeaderCell>{t("TYPE")}</CustomHeaderCell>
                <CustomCell dataKey="company_type">
                  {(rowData: any) => (
                    <span>{displayField(rowData.company_type)}</span>
                  )}
                </CustomCell>
              </Column>

              <Column width={160} resizable>
                <CustomHeaderCell>{t("OWNER")}</CustomHeaderCell>
                <OwnerCell rowData={(rowData: any) => rowData} />
              </Column>

              <Column width={160}>
                <CustomHeaderCell>{t("CONTACT_PERSON")}</CustomHeaderCell>
                <ContactPersonCell rowData={(rowData: any) => rowData} />
              </Column>
              <Column width={120}>
                <CustomHeaderCell>{t("PHONE")}</CustomHeaderCell>
                <PhoneCell
                  positionFromRight="30%"
                  rowData={(rowData: any) => rowData}
                />
              </Column>
              <Column width={160} resizable>
                <CustomHeaderCell>{t("ADDRESS")}</CustomHeaderCell>
                <AddressCell rowData={(rowData: any) => rowData} />
              </Column>
              <Column width={60} sortable resizable>
                <CustomHeaderCell>{t("STORE")}</CustomHeaderCell>
                <CustomCell dataKey="store_count" />
              </Column>
              <Column width={80} resizable>
                <CustomHeaderCell>{t("TAX_NO")}</CustomHeaderCell>
                <CustomCell dataKey="tax_no">
                  {(rowData: any) => (
                    <span title={displayField(rowData.tax_no)}>
                      {displayField(rowData.tax_no)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={150} fixed="right">
                <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                <Cell>
                  {(rowData) => (
                    <CompanySingleAction
                      companyData={rowData}
                      onDeleteCompany={deleteCompany}
                      onPublishCompany={publishCompany}
                      permission={permissionObj}
                      refreshList={refreshListData}
                    />
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            total={total}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
        <ToastContainer />
      </Container>
      <ImageModal
        ref={modalRef}
        modalTitle={t("LOGO")}
        heading={t("LOGO")}
        body={
          <img
            src={selectedCompany.large_logo_url}
            className="main-profile-pic"
            alt={t("LOGO")}
          />
        }
        removePic={() => setOpenPictureDelModal(true)}
      />
      <AlertModal
        open={openPictureDelModal}
        handleClose={(e: any) => {
          if (e == true) {
            deletePic();
          } else {
            setOpenPictureDelModal(false);
            setSelectedCompanyData({});
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DELETE").toLowerCase(),
          entity: t("COMPANY_LOGO").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("DELETE").toLowerCase(),
        })}
      />
    </div>
  );
}

export default Companies;
